import { useToastContext } from '@rtkwlf/fenrir-react';
import type { CredentialPage } from '@rtkwlf/rendall';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useCreateCredentialedScan } from '@apis/rendall/queries/use-create-credentialed-scan';
import { getScannerScanCredentialsQueryKey } from '@apis/rendall/queries/use-scanner-scan-credentials/use-scanner-scan-credentials';
import { useOrganizationContext } from '@packages/legacy/organization-context';
import { TOAST_DURATION } from 'ae-constants/toast';

import { OPTIMISTIC_UPDATE_PREFIX } from './constants';
import type { CredentialedScanningRow } from './types';

export const useCreateCredentialedScanWrapper = (scannerId: string) => {
  const { t } = useTranslation('telemetryScannerConfig');
  const { addToast } = useToastContext();
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganizationContext();
  const queryKey = getScannerScanCredentialsQueryKey(
    selectedOrganization.id,
    scannerId,
  );

  return useCreateCredentialedScan(scannerId, {
    onError: () => {
      addToast({
        appearance: 'danger',
        duration: TOAST_DURATION,
        message: t('credentialedScanning.error'),
        title: t('notifications.error'),
      });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey,
      });
      return {};
    },
    onSuccess: (_data, variables) => {
      const newItem: CredentialedScanningRow = {
        credScanningTypeStr: variables.targets.join(', '),
        description: variables.description ?? '',
        id: `${OPTIMISTIC_UPDATE_PREFIX}-${new Date().getTime()}`,
        name: variables.name,
        targetStr: variables.type,
        targets: variables.targets,
        type: variables.type,
      };

      queryClient.setQueryData(queryKey, (prevData: CredentialPage) => ({
        ...prevData,
        items: [...prevData.items, newItem],
      }));

      addToast({
        appearance: 'success',
        duration: TOAST_DURATION,
        message: t('credentialedScanning.success'),
        title: t('notifications.success'),
      });
    },
  });
};
